import React, { useState, useEffect } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Row, Col, Alert, FormGroup, Label, Input, Progress
} from 'reactstrap';
import { ordersAPI } from '../../services/api';

const UploadQuoteOrderModal = ({ isOpen, toggle, quote, onSuccess }) => {
  const [step, setStep] = useState(1); // 1=subir PDF, 2=revisar y confirmar
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Datos del formulario (editables por el usuario)
  const [formData, setFormData] = useState({
    order_number: '',
    order_date: '',
    subtotal: '',
    amount_includes_tax: false,
    billing_name: '',
    billing_rfc: '',
    notes: '',
  });

  // Reset al cerrar
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setPdfFile(null);
      setError(null);
      setFormData({
        order_number: '',
        order_date: '',
        subtotal: '',
        amount_includes_tax: false,
        billing_name: '',
        billing_rfc: '',
        notes: '',
      });
    }
  }, [isOpen]);

  // Pre-llenar datos de facturación del quote
  useEffect(() => {
    if (quote && isOpen) {
      setFormData(prev => ({
        ...prev,
        billing_name: quote.items?.client_info?.client_name || quote.client_name || '',
      }));
    }
  }, [quote, isOpen]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type !== 'application/pdf') {
      setError('Por favor selecciona un archivo PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo no puede superar 10MB');
      return;
    }
    setPdfFile(file);
    setError(null);
    setStep(2);
  };

  const handleSkipPDF = () => {
    setPdfFile(null);
    setStep(2);
  };

  const handleConfirm = async () => {
    if (!formData.order_number.trim()) {
      setError('El número de orden es obligatorio');
      return;
    }
    if (!formData.order_date) {
      setError('La fecha de orden es obligatoria');
      return;
    }
    if (!formData.subtotal || parseFloat(formData.subtotal) <= 0) {
      setError('El monto de la orden debe ser mayor a 0');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = new FormData();
      data.append('quote_id', quote.id);
      data.append('order_number', formData.order_number.trim());
      data.append('order_date', formData.order_date);
      data.append('subtotal', formData.subtotal);
      data.append('amount_includes_tax', String(formData.amount_includes_tax));
      if (formData.billing_name) data.append('billing_name', formData.billing_name);
      if (formData.billing_rfc) data.append('billing_rfc', formData.billing_rfc);
      if (formData.notes) data.append('notes', formData.notes);
      if (pdfFile) data.append('pdf_file', pdfFile);

      const response = await ordersAPI.uploadForQuote(data);
      onSuccess(response.data.order);
    } catch (err) {
      console.error('Error al vincular OC:', err);
      const errData = err.response?.data;
      if (errData?.error && typeof errData.error === 'object') {
        const msgs = Object.entries(errData.error).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
        setError(msgs.join(' | '));
      } else {
        setError(errData?.error || 'Error al crear la orden');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) =>
    parseFloat(val || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

  const coveredAmount = parseFloat(quote?.covered_amount || 0);
  const quoteSubtotal = parseFloat(quote?.subtotal || 0);
  const quoteTotal = parseFloat(quote?.total || quoteSubtotal * 1.16);
  const remainingAmount = parseFloat(quote?.remaining_amount || quoteSubtotal);
  const coveragePercent = quoteSubtotal > 0 ? Math.min((coveredAmount / quoteSubtotal) * 100, 100) : 0;
  const enteredAmount = parseFloat(formData.subtotal || 0) || 0;
  const previewSubtotal = formData.amount_includes_tax ? enteredAmount / 1.16 : enteredAmount;
  const previewTax = previewSubtotal * 0.16;
  const previewTotal = previewSubtotal + previewTax;
  const suggestedEnteredAmount = formData.amount_includes_tax
    ? remainingAmount * 1.16
    : remainingAmount;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
      <ModalHeader toggle={toggle} className="bg-gradient-info pb-3">
        <span className="text-white">
          <i className="ni ni-cloud-upload-96 me-2"></i>
          Subir Orden de Compra
        </span>
      </ModalHeader>

      <ModalBody>
        {error && <Alert color="danger" className="mb-3">{error}</Alert>}

        {/* Contexto de la cotización */}
        <div className="bg-light rounded p-3 mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <strong className="text-dark">{quote?.proposal_number}</strong>
              <span className="text-muted ms-2">
                — Sin IVA: {formatCurrency(quoteSubtotal)} · Con IVA: {formatCurrency(quoteTotal)}
              </span>
            </div>
            <span className={`badge badge-${quote?.is_fully_covered ? 'success' : 'warning'}`}>
              {quote?.is_fully_covered ? 'Cubierta' : `Falta ${formatCurrency(remainingAmount)}`}
            </span>
          </div>
          <Progress
            value={coveragePercent}
            color={quote?.is_fully_covered ? 'success' : 'warning'}
            className="mb-1"
            style={{ height: '8px' }}
          />
          <small className="text-muted">
            Cubierto: {formatCurrency(coveredAmount)} de {formatCurrency(quoteSubtotal)} ({Math.round(coveragePercent)}%)
            {quote?.orders_count > 0 && ` · ${quote.orders_count} OC${quote.orders_count > 1 ? 's' : ''} vinculada${quote.orders_count > 1 ? 's' : ''}`}
          </small>
        </div>

        {/* PASO 1: Subir PDF */}
        {step === 1 && (
          <div>
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 rounded text-center p-5 ${dragActive ? 'border-primary bg-light' : 'border-dashed'}`}
              style={{ borderStyle: 'dashed', cursor: 'pointer' }}
              onClick={() => document.getElementById('oc-pdf-input').click()}
            >
              <i className="ni ni-cloud-upload-96 text-info" style={{ fontSize: '2rem' }}></i>
              <p className="mt-2 mb-1 font-weight-bold">Arrastra el PDF de la OC aquí</p>
              <p className="text-muted text-sm">o haz clic para seleccionar</p>
              <input
                id="oc-pdf-input"
                type="file"
                accept=".pdf"
                className="d-none"
                onChange={handleFileInput}
              />
            </div>
            <div className="text-center mt-3">
              <Button color="link" size="sm" onClick={handleSkipPDF}>
                Ingresar datos manualmente (sin PDF)
              </Button>
            </div>
          </div>
        )}

        {/* PASO 2: Formulario de datos */}
        {step === 2 && (
          <div>
            {pdfFile && (
              <Alert color="success" className="mb-3">
                <i className="ni ni-check-bold me-2"></i>
                PDF seleccionado: <strong>{pdfFile.name}</strong>
                <Button color="link" size="sm" className="p-0 ms-2" onClick={() => { setPdfFile(null); setStep(1); }}>
                  Cambiar
                </Button>
              </Alert>
            )}
            {!pdfFile && (
              <Alert color="warning" className="mb-3">
                <i className="ni ni-alert-circle-exc me-2"></i>
                Sin PDF — se creará la orden con datos manuales.
                <Button color="link" size="sm" className="p-0 ms-2" onClick={() => setStep(1)}>
                  Subir PDF
                </Button>
              </Alert>
            )}

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label className="font-weight-bold">Número de Orden *</Label>
                  <Input
                    type="text"
                    placeholder="Ej. 4500123456"
                    value={formData.order_number}
                    onChange={e => setFormData(p => ({ ...p, order_number: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="font-weight-bold">Fecha de Orden *</Label>
                  <Input
                    type="date"
                    value={formData.order_date}
                    onChange={e => setFormData(p => ({ ...p, order_date: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="font-weight-bold">Monto capturado en la OC *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder={remainingAmount > 0 ? suggestedEnteredAmount.toFixed(2) : '0.00'}
                    value={formData.subtotal}
                    onChange={e => setFormData(p => ({ ...p, subtotal: e.target.value }))}
                  />
                  {remainingAmount > 0 && (
                    <small className="text-muted">
                      Pendiente sin IVA: {formatCurrency(remainingAmount)}
                      <Button color="link" size="sm" className="p-0 ms-1"
                        onClick={() => setFormData(p => ({ ...p, subtotal: suggestedEnteredAmount.toFixed(2) }))}>
                        Usar monto pendiente
                      </Button>
                    </small>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="font-weight-bold">¿El monto incluye IVA? *</Label>
                  <Input
                    type="select"
                    value={String(formData.amount_includes_tax)}
                    onChange={e => setFormData(p => ({
                      ...p,
                      amount_includes_tax: e.target.value === 'true',
                    }))}
                  >
                    <option value="false">No, es subtotal sin IVA</option>
                    <option value="true">Sí, es total con IVA</option>
                  </Input>
                </FormGroup>
              </Col>
              {enteredAmount > 0 && (
                <Col md="12">
                  <Alert color="info" className="py-2 mb-3">
                    <strong>Vista previa:</strong>{' '}
                    Subtotal {formatCurrency(previewSubtotal)} · IVA {formatCurrency(previewTax)} · Total {formatCurrency(previewTotal)}
                  </Alert>
                </Col>
              )}
              <Col md="6">
                <FormGroup>
                  <Label>Razón Social del Cliente</Label>
                  <Input
                    type="text"
                    value={formData.billing_name}
                    onChange={e => setFormData(p => ({ ...p, billing_name: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>RFC del Cliente</Label>
                  <Input
                    type="text"
                    value={formData.billing_rfc}
                    onChange={e => setFormData(p => ({ ...p, billing_rfc: e.target.value }))}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Notas (opcional)</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    value={formData.notes}
                    onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancelar
        </Button>
        {step === 2 && (
          <Button color="primary" onClick={handleConfirm} disabled={loading}>
            {loading ? (
              <><span className="spinner-border spinner-border-sm me-2" />&nbsp;Guardando...</>
            ) : (
              <><i className="ni ni-check-bold me-1"></i> Confirmar y Vincular</>
            )}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UploadQuoteOrderModal;
